import React from "react";
import { Exercise } from "./types";
import {
  Box,
  Card,
  Chip,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import TimerIcon from "@mui/icons-material/Timer";
import RepeatIcon from "@mui/icons-material/Repeat";

import { millisToReadableFormat } from "../../utils/commons";

export const ExerciseSetList = ({
  viewOnly,
  exercises,
  onExerciseDeleteClicked,
  onExerciseEditClicked,
}: {
  viewOnly?: boolean;
  exercises: Array<Exercise>;
  onExerciseDeleteClicked?: (id: number) => void;
  onExerciseEditClicked?: (id: number) => void;
}) => {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        flex: 1,
        minHeight: 0,
      }}
    >
      <Typography
        variant="subtitle1"
        fontWeight={600}
        sx={{ flexShrink: 0, pb: 1.5 }}
      >
        Exercises
      </Typography>
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          minHeight: 0,
          scrollbarWidth: "thin",
        }}
      >
        {Array.isArray(exercises) && exercises.length > 0
          ? exercises.map((exercise, idx) => (
              <Card
                key={exercise.id ?? idx}
                elevation={0}
                sx={{
                  mb: 1,
                  p: 1.5,
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: "divider",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  transition: "border-color 0.15s ease",
                  "&:hover": { borderColor: "secondary.main" },
                }}
              >
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography variant="body2" fontWeight={600} noWrap>
                    {exercise.name}
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      mt: 0.5,
                    }}
                  >
                    {exercise.type === "TIME_BOUND" ? (
                      <>
                        <TimerIcon
                          sx={{ fontSize: 14, color: "text.secondary" }}
                        />
                        <Chip
                          label={millisToReadableFormat(exercise.duration)}
                          size="small"
                          sx={{ height: 20, fontSize: "0.7rem" }}
                        />
                        <Chip
                          label={`Rest ${millisToReadableFormat(
                            exercise.breakTime
                          )}`}
                          size="small"
                          variant="outlined"
                          sx={{ height: 20, fontSize: "0.7rem" }}
                        />
                      </>
                    ) : (
                      <>
                        <RepeatIcon
                          sx={{ fontSize: 14, color: "text.secondary" }}
                        />
                        <Chip
                          label={`x${exercise.repetitions}`}
                          size="small"
                          color="secondary"
                          sx={{
                            height: 20,
                            fontSize: "0.7rem",
                            fontWeight: 700,
                          }}
                        />
                      </>
                    )}
                  </Box>
                </Box>
                {!viewOnly && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      ml: 1,
                      flexShrink: 0,
                    }}
                  >
                    <Tooltip title="Edit">
                      <IconButton
                        size="small"
                        onClick={() => onExerciseEditClicked?.(exercise.id)}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        onClick={() => onExerciseDeleteClicked?.(exercise.id)}
                        sx={{
                          color: "error.main",
                          "&:hover": { bgcolor: "rgba(244,67,54,0.08)" },
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                )}
              </Card>
            ))
          : null}
      </Box>
    </Box>
  );
};
